/**
 * Schemas Zod compartilhados entre backend e frontend.
 *
 * O mesmo schema valida a requisição no Fastify, tipa o handler, gera a
 * documentação OpenAPI e alimenta o React Hook Form. Uma regra, um lugar.
 */
import { z } from 'zod';
import { CODIGOS_STATUS, FORMAS_PAGAMENTO } from './status.js';
import { ehTelefoneValido, normalizarTelefone } from './phone.js';
import { CODIGOS_SECAO } from './secoes.js';
/* ------------------------------------------------------------------ */
/* Primitivos                                                          */
/* ------------------------------------------------------------------ */
/** Telefone em qualquer formato de entrada; sai sempre em E.164 sem '+'. */
export const zTelefone = z
    .string()
    .min(1, 'Telefone é obrigatório')
    .refine(ehTelefoneValido, 'Telefone inválido — informe DDD + 9 dígitos')
    .transform((v) => normalizarTelefone(v));
/** Valor monetário sempre em centavos inteiros. */
export const zCentavos = z.number().int('Valor deve ser inteiro (centavos)');
export const zCentavosPositivo = zCentavos.positive('Valor deve ser maior que zero');
export const zCentavosNaoNegativo = zCentavos.nonnegative('Valor não pode ser negativo');
export const zDataISO = z
    .string()
    .refine((v) => !Number.isNaN(Date.parse(v)), 'Data inválida');
const textoObrigatorio = (campo, max = 255) => z.string().trim().min(1, `${campo} é obrigatório`).max(max);
/* ------------------------------------------------------------------ */
/* Paginação                                                           */
/* ------------------------------------------------------------------ */
export const paginacaoQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(10),
});
/* ------------------------------------------------------------------ */
/* Ordens de serviço                                                   */
/* ------------------------------------------------------------------ */
export const listarOsQuerySchema = paginacaoQuerySchema.extend({
    /** Busca textual em nome, sobrenome, aparelho, marca, modelo e telefone. */
    q: z.string().trim().min(1).optional(),
    osNumber: z.coerce.number().int().positive().optional(),
    status: z.string().trim().min(1).optional(),
    tecnicoId: z.coerce.number().int().positive().optional(),
    dataInicio: zDataISO.optional(),
    dataFim: zDataISO.optional(),
});
export const criarOsSchema = z.object({
    telefone: zTelefone,
    nome: textoObrigatorio('Nome', 120),
    sobrenome: z.string().trim().max(120).default(''),
    aparelho: textoObrigatorio('Aparelho'),
    marca: textoObrigatorio('Marca'),
    modelo: textoObrigatorio('Modelo'),
    defeito: textoObrigatorio('Defeito', 4000),
    observacoes: z.string().trim().max(4000).default(''),
    /** Quando omitido, o servidor aplica as regras de `tecnico_regras`. */
    tecnicoId: z.number().int().positive().nullish(),
    /** Dispara a impressão física via n8n. */
    imprimir: z.boolean().default(true),
});
export const pagamentoSchema = z.object({
    valorCentavos: zCentavosPositivo,
    forma: z.enum(FORMAS_PAGAMENTO),
    parcelas: z.number().int().min(1).max(12).optional(),
});
export const atualizarStatusSchema = z
    .object({
    status: z.enum(CODIGOS_STATUS),
    /** ISO 8601. `null` quando o status não exige data de saída. */
    saida: zDataISO.nullish(),
    /** Gravado em `pagamento` E em `caixa`, na mesma transação (corrige #5/#9). */
    pagamento: pagamentoSchema.nullish(),
    observacao: z.string().trim().max(500).optional(),
})
    .strict();
/* ------------------------------------------------------------------ */
/* Orçamento                                                           */
/* ------------------------------------------------------------------ */
export const itemDescricaoSchema = z.object({
    descricao: z.string().trim().min(1).max(2000),
});
export const itemValorSchema = z.object({
    descricao: z.string().trim().min(1).max(2000),
    valorCentavos: zCentavosNaoNegativo,
});
/** Semântica replace-all, executada em transação única. */
export const orcamentoSchema = z.object({
    defeitos: z.array(itemDescricaoSchema).max(100).default([]),
    servicos: z.array(itemValorSchema).max(100).default([]),
    pecas: z.array(itemValorSchema).max(100).default([]),
});
/* ------------------------------------------------------------------ */
/* Caixa                                                               */
/* ------------------------------------------------------------------ */
export const TIPOS_CAIXA = ['entrada', 'saida'];
export const ORIGENS_CAIXA = ['os', 'venda', 'despesa'];
export const criarLancamentoCaixaSchema = z.object({
    origem: z.enum(ORIGENS_CAIXA),
    /** Obrigatório quando `origem === 'os'`. */
    osNumber: z.number().int().positive().nullish(),
    tipo: z.enum(TIPOS_CAIXA),
    /** Sempre positivo no payload — o SINAL é aplicado pelo servidor conforme `tipo`. */
    valorCentavos: zCentavosPositivo,
    descricao: textoObrigatorio('Descrição', 2000),
    forma: z.enum(FORMAS_PAGAMENTO),
    parcelas: z.number().int().min(1).max(12).optional(),
}).refine((v) => v.origem !== 'os' || (v.osNumber != null && v.osNumber > 0), { message: 'Informe o número da OS para lançamentos de origem "os"', path: ['osNumber'] });
export const listarCaixaQuerySchema = paginacaoQuerySchema.extend({
    dataInicio: zDataISO.optional(),
    dataFim: zDataISO.optional(),
    tipo: z.enum(TIPOS_CAIXA).optional(),
    origem: z.enum(ORIGENS_CAIXA).optional(),
    osNumber: z.coerce.number().int().positive().optional(),
});
/* ------------------------------------------------------------------ */
/* Clientes e catálogos                                                */
/* ------------------------------------------------------------------ */
export const listarClientesQuerySchema = paginacaoQuerySchema.extend({
    q: z.string().trim().min(1).optional(),
});
export const criarItemCatalogoSchema = z.object({
    nome: textoObrigatorio('Nome'),
});
/* ------------------------------------------------------------------ */
/* Autenticação                                                        */
/* ------------------------------------------------------------------ */
export const PAPEIS = ['admin', 'operador', 'tecnico', 'leitura'];
/**
 * 72 é o limite do bcrypt, o algoritmo que guarda a senha no Postgres: o
 * que passa disso é ignorado no hash. Aceitar 200 caracteres seria
 * prometer uma força que não existe.
 */
const senhaSchema = z
    .string()
    .min(8, 'Senha deve ter ao menos 8 caracteres')
    .max(72, 'Senha deve ter no máximo 72 caracteres');
/**
 * O login é o nome de usuário, não o e-mail: quem entra no sistema digita
 * isto no balcão dez vezes por dia. As mesmas regras estão no CHECK de
 * `auth_usuarios` (`docs/postgrest/02_auth_login.sql`) — aqui elas existem
 * para o erro aparecer no campo, não como 500 vindo do banco.
 */
export const nomeDeUsuarioSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Usuário deve ter ao menos 3 caracteres')
    .max(40, 'Usuário deve ter no máximo 40 caracteres')
    .regex(/^[a-z0-9._-]+$/, 'Usuário aceita apenas letras, números, ponto, hífen e sublinhado');
export const loginSchema = z.object({
    usuario: nomeDeUsuarioSchema,
    senha: senhaSchema,
});
export const alterarSenhaSchema = z.object({
    senhaAtual: z.string().min(1, 'Informe a senha atual'),
    senhaNova: senhaSchema,
});
/* ------------------------------------------------------------------ */
/* Administração de contas (papel `admin`)                             */
/* ------------------------------------------------------------------ */
/**
 * A senha informada aqui é sempre PROVISÓRIA: quem cria a conta é outra
 * pessoa, e o banco marca `deve_trocar_senha`. Quem entrar troca antes de
 * usar o sistema — então esta senha só precisa chegar até o dono da conta.
 */
export const criarUsuarioSchema = z.object({
    usuario: nomeDeUsuarioSchema,
    nome: textoObrigatorio('Nome', 120),
    senha: senhaSchema,
    papel: z.enum(PAPEIS),
    /** Opcional: o login é pelo usuário, o e-mail é só cadastro. */
    email: z.string().trim().toLowerCase().email('E-mail inválido').max(160).optional(),
    /** Amarra a conta a um técnico do catálogo, para o filtro do monitor. */
    tecnicoId: z.number().int().positive().nullish(),
});
export const redefinirSenhaSchema = z.object({ senhaNova: senhaSchema });
export const definirAtivoSchema = z.object({ ativo: z.boolean() });
/**
 * Seções do menu que a conta enxerga.
 *
 * `null` devolve a conta ao padrão do papel — é a saída de "eu mexi demais,
 * quero como era". A lista, quando vem, precisa ter ao menos um item: conta
 * que entra e não vê tela nenhuma é conta desativada com passos a mais, e
 * desativar já existe em `/usuarios/:login/ativo`.
 */
export const definirSecoesSchema = z.object({
    secoes: z
        .array(z.enum(CODIGOS_SECAO))
        .min(1, 'Marque ao menos uma seção — ou use o padrão do perfil')
        .max(CODIGOS_SECAO.length)
        .nullable(),
});
/* ------------------------------------------------------------------ */
/* Mensagens                                                           */
/* ------------------------------------------------------------------ */
export const enviarMensagemSchema = z.object({
    osNumber: z.number().int().positive(),
    template: z.string().trim().min(1).max(40),
    valorCentavos: zCentavosNaoNegativo.optional(),
    /** Variáveis extras para interpolar no corpo do template. */
    variaveis: z.record(z.string()).optional(),
});
/**
 * Prévia — aceita um orçamento AINDA NÃO SALVO.
 *
 * Sem este campo a prévia lê o banco: o operador digita o orçamento, pede a
 * prévia e recebe o texto do orçamento ANTERIOR. Conferir uma coisa e o
 * cliente receber outra é justamente o que a prévia existe para evitar.
 *
 * O override é só da prévia, de propósito. `/mensagens/enviar` continua
 * montando a mensagem a partir do que está GRAVADO na OS — assim o WhatsApp
 * que o cliente recebe e o orçamento que ficou na OS nunca divergem.
 */
export const previaMensagemSchema = enviarMensagemSchema.extend({
    orcamento: orcamentoSchema.optional(),
});
/** Relatórios com recorte de período. */
export const periodoQuerySchema = z.object({
    dataInicio: zDataISO.optional(),
    dataFim: zDataISO.optional(),
});
/* ------------------------------------------------------------------ */
/* Consulta pública                                                    */
/* ------------------------------------------------------------------ */
/**
 * O token do link de acompanhamento: 16 bytes em hexadecimal.
 *
 * O formato é validado na BORDA, antes de qualquer viagem ao banco, e o
 * mesmo `CHECK` existe do lado do Postgres (`04_consulta_publica.sql`).
 * Duplicar a regra aqui não é descuido: esta é a única rota da API que
 * responde sem sessão, e um parâmetro que não casa com o formato precisa
 * morrer no Fastify — não virar uma chamada ao banco por tentativa de
 * quem estiver varrendo.
 */
export const tokenPublicoSchema = z
    .string()
    .regex(/^[0-9a-f]{32}$/, 'Link de acompanhamento inválido.');
export const paramsTokenPublicoSchema = z.object({ token: tokenPublicoSchema });
/**
 * `rotacionar: true` troca o token de um link que ainda está ativo — o
 * caso do endereço que vazou para a conversa errada. Sem ele, emitir é
 * idempotente e devolve o link que já existe, porque o botão da ficha é o
 * mesmo para gerar e para copiar de novo.
 */
export const emitirLinkPublicoSchema = z.object({
    rotacionar: z.boolean().default(false),
});
/* ------------------------------------------------------------------ */
/* Configuração editável pela tela                                     */
/* ------------------------------------------------------------------ */
/**
 * Os termos de serviço que vão junto com a OS no WhatsApp.
 *
 * O limite de 3.000 caracteres não é do produto: é do WhatsApp. Mensagem
 * com botões (`send-button-actions`) tem teto de corpo bem menor que a de
 * texto puro, e o que passa do teto não chega truncado — não chega. O
 * mínimo de 20 existe pelo mesmo motivo que `OS_TERMOS` vazio conta como
 * desligado: dois botões de aceite embaixo de três palavras é pior do que
 * não mandar nada.
 *
 * `habilitado: false` é o jeito de parar de mandar SEM perder o texto —
 * o campo continua gravado, esperando ser religado.
 */
export const salvarTermosSchema = z.object({
    texto: z
        .string()
        .trim()
        .min(20, 'O texto está curto demais para valer como termo de serviço.')
        .max(3000, 'O WhatsApp não entrega mensagem com botões acima de ~3.000 caracteres.'),
    habilitado: z.boolean(),
});
//# sourceMappingURL=schemas.js.map